/**
 * What an organisation IS.
 *
 * Mirrors `HHNodeServer/src/constants/organisationTypes.js` — keep the two in
 * step. The server is the authority (a CHECK constraint on
 * `organisation_stakeholder_types` enforces the same six values); this copy
 * exists so a picker can label and filter without a round trip.
 *
 * The distinction worth holding on to: these describe what a business *is*,
 * never what it *does in a particular relationship*. Whether an organisation
 * is a licensor is already recorded in `cultivar_licensors`, and whether it is
 * a licensee is in `regional_licenses` — both checkable at any moment, which
 * is precisely why a tag sitting beside them can only ever go stale. Ask the
 * relationship.
 */

export interface OrganisationType {
  value: string;
  label: string;
  description: string;
}

export const ORGANISATION_TYPES: OrganisationType[] = [
  {
    value: 'orchard',
    label: 'Orchard',
    description: 'Grows fruit. Buys trees, plants blocks, holds planting rights.',
  },
  {
    value: 'nursery',
    label: 'Nursery',
    description: 'Propagates trees. Lists cultivars, takes orders, holds propagation rights.',
  },
  {
    value: 'marketer',
    label: 'Marketer',
    description: 'Markets and sells fruit on a grower’s behalf.',
  },
  {
    value: 'supplier',
    label: 'Supplier',
    description: 'Sells inputs — chemical, equipment, consumables.',
  },
  {
    value: 'packhouse',
    label: 'Packhouse',
    description: 'Packs and grades fruit.',
  },
  {
    value: 'breeder',
    label: 'Breeder',
    description: 'Breeds cultivars and originates the rights in them.',
  },
];

export const VALID_ORGANISATION_TYPES = ORGANISATION_TYPES.map(t => t.value);

/** The filter a picker passes when it wants "an orchard". */
export const ORCHARD_TYPE_FILTER = 'orchard';

/** The filter a picker passes when it wants "a nursery". */
export const NURSERY_TYPE_FILTER = 'nursery';

const LABELS = new Map(ORGANISATION_TYPES.map(t => [t.value, t.label]));

/**
 * Display name for a stored type. Legacy values may still arrive from a client
 * that has not reloaded since the changeover, so they get a readable label
 * rather than leaking a raw enum into the page.
 */
export function organisationTypeLabel(value: string): string {
  return LABELS.get(value) || value.replace(/_/g, ' ');
}
